import * as React from 'react';
import { useRef } from 'react';
import { X } from 'lucide-react';
import { useFocusTrap } from '../../hooks/useFocusTrap';

interface ImageLightboxProps {
    src: string;
    alt: string;
    onClose: () => void;
}

/**
 * ImageLightbox — a minimal single-image zoom overlay. Mirrors the gym-profile
 * gallery lightbox visual treatment (fixed inset-0 bg-black/90, contained image,
 * top-right close) but is purpose-built for ONE image and is a11y-complete:
 * Escape-to-close, focus trap, and body scroll-lock via the shared useFocusTrap
 * (the inline gym overlay omits all three). Backdrop / X / Escape all close.
 */
const ImageLightbox: React.FC<ImageLightboxProps> = ({ src, alt, onClose }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    useFocusTrap({ active: true, containerRef, onEscape: onClose, lockScroll: true, restoreFocus: true });

    return (
        <div
            ref={containerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Enlarged image"
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <button
                type="button"
                onClick={onClose}
                aria-label="Close image"
                className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            >
                <X size={32} />
            </button>
            <img
                src={src}
                alt={alt}
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
            />
        </div>
    );
};

export default ImageLightbox;
